import React, { useEffect, useState, useContext, useRef } from "react";
import Head from "../Head";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import axiosConfig from "../../utils/axiosConfig";
import GlobalConfig from "../../context/GlobalConfig";
import Footer from "../../components/Footer";
import LivenessAnalysing from "../../components/LivenessAnalysing";
import JourneyConfig from "../../context/JourneyConfig";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import GoogleIcon from "../../components/GoogleIcon";
import { languageData } from "../../constants/languageData";
import { getMukhamLocale } from "../../constants/languages";

const ActiveLiveness = ({ stepperFunc, steps, currentStep, currentDocumentStep, isMobile }) => {
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const { journeyId } = useParams();
  const { updateJourney } = useContext(JourneyConfig);
  const { config } = useContext(GlobalConfig);
  const color = config?.branding?.accentColor;
  const bgColor = "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  // Head writes the picked language to sessionStorage and calls setConfig, so this
  // re-reads on the re-render that follows a language change.
  const language = sessionStorage.getItem("language") ?? "en";
  const mukhamLocale = getMukhamLocale(language);
  const requestedLocale = useRef(null);

  useEffect(() => {
    // Mukham bakes the locale into the SDK token, so a language change needs a new
    // token and a fresh iframe. Any other re-render (config updates, StrictMode)
    // must not restart the camera, hence the guard on the locale already requested.
    if (requestedLocale.current === mukhamLocale) return;
    requestedLocale.current = mukhamLocale;

    const generateOtpLivelinessUrl = async () => {
      try {
        setLoading(true);

        const response = await axiosConfig.post(
          "/otk-service/generate-opt-liveliness-sdk-url",
          { journeyId , activeLivenessType:config?.faceCapture?.activeLivenessType || "OTP", locale: mukhamLocale }
        );

        // Another language was picked while this was in flight; that request owns the
        // screen now, so drop this response instead of flashing the wrong language.
        if (requestedLocale.current !== mukhamLocale) return;

        const url = response?.data?.result?.url || response?.data?.url || response?.data?.result;
        setLoading(false);

        if (url) {
          setIframeUrl(url);
        } else {
          // Allow a retry on the next language change; the iframe already on screen
          // (if any) keeps working in the previous language.
          requestedLocale.current = null;
          console.error("URL not found in API response");
        }
      } catch (err) {
        if (requestedLocale.current !== mukhamLocale) return;
        requestedLocale.current = null;
        setLoading(false);
        console.error("Error generating OTP liveliness token:", err);
      }
    };

    if (journeyId) {
      generateOtpLivelinessUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, mukhamLocale]);

  const navigateToImageCheck = () => {
    setTimeout(() => {
      stepperFunc(steps[0]);
    }, 2000);
  };

  useEffect(() => {
    const handleMessage = async (event) => {
      const data = event?.data;
      console.log("Mukham data received :", data);

      if (data?.source === "mukham" && data?.message === "Permission denied") {
        setPermissionDenied(true);
        return;
      }

      // Check if source is "mukham"
      if (data?.source === "mukham" && data?.message !== "Permission denied") {
        const personId = data?.person_id;      

        try {
          setPosting(true);

          const response = await updateJourney(
            "FACE_CAPTURE",
            {
              currentDocumentStep: currentDocumentStep + 1,
              personId,
            },
            currentStep
          );
          const passiveMessage = response?.passiveLiveliness?.message?.message || "";
          const hasMultipleFaces = passiveMessage.toLowerCase().includes("multiple faces found");
          const isDeepFakeAiGenerated = response?.deepfakeAnalysis?.isAiGenerated;
          const shouldTriggerError =
            (response?.isFaceCovered && response?.faceDetections) ||
            response?.passiveLiveliness?.liveness === false ||
            response?.faceMatch === false ||
            response?.faceMatch2 === false ||
            isDeepFakeAiGenerated === true ||
            hasMultipleFaces || response?.eyeClosureDetected || response?.activeLivenessCheckFailed;
          if (shouldTriggerError) {
            sessionStorage.setItem("error", "true");
            sessionStorage.setItem("errorCategory", "livenessCheckError");
            sessionStorage.setItem("errorReason", JSON.stringify(response)); // Safely store the object as string
            const currentStepFromStorage = sessionStorage.getItem("currentStep");
            stepperFunc(
              currentStepFromStorage
                ? parseInt(currentStepFromStorage)
                : 0
            );
          }
          else {
            navigateToImageCheck();
          }
        } catch (error) {
          // If update fails, stay on analysing screen; optionally log
          console.error("Error updating journey from ActiveLiveness:", error);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [currentDocumentStep, currentStep, stepperFunc, steps, updateJourney]);

  // Full-page loader only until there is something to show. Once the page is up, a
  // language change is handled inside the camera pane so the header — and the
  // selector the user just used — stay on screen.
  if (!iframeUrl) {
    return <Loader />;
  }

  if (posting) {
    return <LivenessAnalysing />;
  }

  if (permissionDenied) {
    return (
      <>
        <Head
          stepperFunc={stepperFunc}
          showLanguages={false}
          showBackIcon={false}
          style={{ zIndex: "100" }}
        />
        <div className="parent-container" style={{ paddingTop: 0 }}>
          <div className="main">
            <div className="left-section">
              <GoogleIcon iconName="videocam_off" size={48} style={{ color: "#656565" }} />
            </div>
            <div
              className="right-section md:items-start items-center"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                fontFamily: "Inter, sans-serif",
              }}
            >
              <div className="flex flex-col h-full">
                <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                  {languageData[language]?.activeLiveness?.permissionDeniedTitle ??
                    languageData["en"]?.activeLiveness?.permissionDeniedTitle}
                </div>
                <div className="mt-[32px] mb-4 text-[12px] md:text-[14px] text-[#656565] text-center md:text-left font-inter text-sm font-semibold leading-normal">
                  {languageData[language]?.activeLiveness?.permissionDeniedSubtitle ??
                    languageData["en"]?.activeLiveness?.permissionDeniedSubtitle}
                </div>
              </div>
              <div className="flex flex-col justify-between font-sans w-full">
                <button
                  className="flex flex-row items-center justify-center md:gap-4 gap-6 w-full text-white py-4 px-4 rounded text-[16px] font-sans"
                  style={{
                    backgroundColor: color,
                    marginTop: isMobile ? "12px" : "0px",
                  }}
                  onClick={() => {
                    setPermissionDenied(false);
                    setIframeKey((k) => k + 1);
                  }}
                >
                  Refresh
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
                <Footer style={{ marginTop: "1rem" }} />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head
        stepperFunc={stepperFunc}
        showLanguages={true}
        showBackIcon={false}
        style={{ zIndex: "100" }}
      />
      <div className="parent-container" >
        <div className="main">
          <div className="left-section">
            {loading ? (
              <div
                className="flex h-full w-full items-center justify-center"
                data-testid="liveness-reloading"
              >
                <div className="spinner" />
              </div>
            ) : (
              <iframe
                key={iframeKey}
                src={iframeUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                }}
                title="OTP Liveliness SDK"
                allow="camera; microphone"
              />
            )}
          </div>
          {!isMobile && (
            <div
              className="right-section md:items-start items-center"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 24,
                fontFamily: "Inter, sans-serif",
              }}
            >
              <>
                <div>
                  <div className="flex flex-col md:mt-[130px]">
                    <div className="flex justify-center md:justify-start">
                      <svg
                        width="44"
                        height="44"
                        viewBox="0 0 44 44"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="22"
                          cy="22"
                          r="22"
                          fill={bgColor}
                          style={{ background: bgColor }}
                        />
                        <g clipPath="url(#clip0_1556_28470)">
                          <path
                            d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
                            fill="#1658aa"
                            style={{ fill: color }}
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1556_28470">
                            <rect
                              width="24"
                              height="24"
                              fill="white"
                              transform="translate(10 10.5)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div
                      className="md:mt-3 mt-6 text-[16px] md:text-[28px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0 md:leading-[45px] leading-[24px]"
                      style={{
                        color: color,
                        fontWeight: 600,
                      }}
                    >
                      {languageData[language]?.activeLiveness?.instruction ??
                        languageData["en"]?.activeLiveness?.instruction}
                    </div>
                  </div>
                </div>
                <Footer />
              </>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ActiveLiveness;
